import { useEffect, useRef, useState } from "react";
import { api, inr } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import { toast } from "sonner";
import { PaperPlaneRight, Sparkle, ChatCircleText, ArrowsClockwise, Lightning } from "@phosphor-icons/react";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const PIE_COLORS = ["hsl(var(--tint-terracotta))", "hsl(var(--tint-sage))", "hsl(var(--tint-plum))", "hsl(var(--tint-gold))", "hsl(var(--tint-indigo))", "hsl(var(--tint-teal))", "hsl(var(--tint-clay))"];

const SUGGESTIONS = [
  "What are my top selling products in the last quarter?",
  "How much revenue did wholesale orders generate this month?",
  "Which materials are below reorder level right now?",
  "Compare production output for apparel vs jewellery.",
  "Give me a monthly profit breakdown by category.",
  "How many exchange requests are open and what's the most common reason?",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, busy]);

  const loadSessions = async () => {
    try { const { data } = await api.get("/ai/sessions"); setSessions(data); }
    catch { /* silent */ }
  };

  const openSession = async (sid) => {
    setSessionId(sid);
    try {
      const { data } = await api.get(`/ai/sessions/${sid}`);
      setMessages(data.map((m) => ({ role: m.role, content: m.content })));
    } catch { toast.error("Load failed"); }
  };

  const send = async (question) => {
    const query = (question ?? q).trim();
    if (!query) return;
    setMessages((m) => [...m, { role: "user", content: query }]);
    setQ(""); setBusy(true);
    try {
      const { data } = await api.post("/ai/query", { question: query, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data }]);
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    }
    setBusy(false);
  };

  const newSession = () => { setMessages([]); setSessionId(null); };

  return (
    <div data-testid="ai-assistant-page" className="page-fade">
      <PageHeader
        eyebrow="AI · Claude Sonnet 4.5"
        title="Ask Asymmetry AI"
        description="Ask anything about your business. The assistant reads your live ERP data — sales, inventory, production, finance — and returns answers with charts, tables and follow-up suggestions."
        tint="plum"
        icon={Sparkle}
        actions={<button onClick={newSession} className="btn-ghost" data-testid="ai-new-session"><ChatCircleText size={14}/> New chat</button>}
      />

      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        <aside className="hidden lg:block">
          <div className="label-eyebrow mb-3">Recent</div>
          <div className="space-y-1.5">
            {sessions.length === 0 && <div className="text-xs text-muted-foreground">No conversations yet</div>}
            {sessions.map((s) => (
              <button key={s.session_id} onClick={() => openSession(s.session_id)} data-testid={`ai-session-${s.session_id}`}
                      className={`w-full text-left rounded-lg px-3 py-2.5 border transition-all ${sessionId===s.session_id ? "border-foreground/50 bg-muted/60" : "border-transparent hover:bg-muted/50"}`}>
                <div className="text-sm truncate">{s.first_question}</div>
                <div className="text-[10.5px] text-muted-foreground mt-0.5 tabular">{s.count} messages</div>
              </button>
            ))}
          </div>
        </aside>

        <div className="card-soft p-0 flex flex-col h-[calc(100vh-260px)] min-h-[520px]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 && (
              <div className="max-w-2xl mx-auto py-8">
                <div className="text-center mb-8">
                  <div className="w-14 h-14 rounded-2xl bg-[color:hsl(var(--tint-plum)/0.14)] flex items-center justify-center mx-auto mb-4 text-[color:hsl(var(--tint-plum))]"><Sparkle size={26} weight="duotone"/></div>
                  <div className="display-2">How can I help?</div>
                  <p className="text-sm text-muted-foreground mt-2">Try one of these — or ask anything in your own words.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 stagger">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="ai-suggestion" className="card-soft text-left text-sm hover:border-foreground/40 hover:shadow-[var(--shadow-sm)] transition-all rounded-xl px-4 py-3.5 flex items-start gap-3">
                      <Lightning size={16} className="text-[color:hsl(var(--tint-plum))] shrink-0 mt-0.5" weight="duotone"/>
                      <span>{s}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className="flex">
                {m.role === "user" ? (
                  <div className="chat-user" data-testid={`ai-msg-user-${i}`}>{m.content}</div>
                ) : (
                  <AssistantBubble content={m.content} onFollow={(t) => send(t)} idx={i}/>
                )}
              </div>
            ))}
            {busy && (
              <div className="chat-assistant" data-testid="ai-msg-loading">
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><ArrowsClockwise size={14} className="animate-spin"/> Thinking through your data…</div>
              </div>
            )}
          </div>

          <div className="border-t border-border/60 p-4">
            <form onSubmit={(e)=>{ e.preventDefault(); send(); }} className="flex gap-2 items-center">
              <div className="flex-1 relative">
                <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Ask about sales, inventory, finance, production…"
                       className="input-soft pl-4 pr-14 h-12" data-testid="ai-input" disabled={busy}/>
              </div>
              <button type="submit" disabled={!q.trim() || busy} className="btn-primary h-12 px-6" data-testid="ai-send">
                <PaperPlaneRight size={16} weight="fill"/>
              </button>
            </form>
            <div className="text-[10.5px] text-muted-foreground mt-2 text-center">AI answers are grounded in your live ERP snapshot · Never presented as confirmed facts.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssistantBubble({ content, onFollow, idx }) {
  if (typeof content === "string") return <div className="chat-assistant">{content}</div>;
  const { summary, chart, table, kpis, followups } = content || {};
  return (
    <div className="chat-assistant space-y-4 w-full" data-testid={`ai-msg-assistant-${idx}`}>
      {summary && <div className="text-sm leading-relaxed whitespace-pre-wrap">{summary}</div>}
      {kpis?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          {kpis.map((k, i) => (
            <div key={i} className="rounded-lg border border-border/70 bg-card p-3">
              <div className="label-eyebrow">{k.label}</div>
              <div className="metric-num mt-1.5">{typeof k.value === "number" ? (k.unit === "INR" ? inr(k.value) : k.value.toLocaleString()) : k.value}{k.unit && k.unit !== "INR" ? <span className="text-xs text-muted-foreground ml-1">{k.unit}</span> : ""}</div>
            </div>
          ))}
        </div>
      )}
      {chart && chart.type && chart.type !== "none" && chart.type !== "kpi" && chart.type !== "table" && (
        <div className="bg-card rounded-lg border border-border/70 p-3 mt-3">
          {chart.title && <div className="label-eyebrow mb-2">{chart.title}</div>}
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
              {renderChart(chart)}
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {table?.length > 0 && (
        <div className="card-soft p-0 overflow-x-auto mt-3">
          <table className="table-modern text-sm">
            <thead><tr>{Object.keys(table[0]).map((k) => <th key={k} className="capitalize">{k.replace(/_/g," ")}</th>)}</tr></thead>
            <tbody>
              {table.slice(0, 30).map((row, i) => (
                <tr key={i}>{Object.values(row).map((v, j) => <td key={j} className="tabular">{typeof v === "number" ? v.toLocaleString() : String(v)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {followups?.length > 0 && (
        <div className="mt-2">
          <div className="label-eyebrow mb-2">Ask a follow-up</div>
          <div className="flex flex-wrap gap-2">
            {followups.map((f, i) => <button key={i} onClick={() => onFollow(f)} className="pill-toggle" data-testid="ai-followup">{f}</button>)}
          </div>
        </div>
      )}
    </div>
  );
}

function renderChart(chart) {
  const data = (chart.series?.[0]?.data || []).map((d) => ({ ...d, name: d.x }));
  if (chart.type === "line") {
    return (
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false}/>
        <XAxis dataKey="name" fontSize={11} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false}/>
        <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false}/>
        <Tooltip contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:10,boxShadow:"var(--shadow-lg)",fontSize:12}}/>
        {chart.series?.map((s, i) => <Line key={s.name} dataKey="y" data={s.data.map(d=>({...d,name:d.x}))} name={s.name} stroke={PIE_COLORS[i%PIE_COLORS.length]} strokeWidth={1.75} dot={{r:2.5}} animationDuration={700}/>)}
      </LineChart>
    );
  }
  if (chart.type === "bar") {
    return (
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false}/>
        <XAxis dataKey="name" fontSize={11} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false}/>
        <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false}/>
        <Tooltip contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:10,boxShadow:"var(--shadow-lg)",fontSize:12}}/>
        <Bar dataKey="y" fill="hsl(var(--tint-plum))" radius={[6,6,0,0]} animationDuration={700}/>
      </BarChart>
    );
  }
  if (chart.type === "pie") {
    return (
      <PieChart>
        <Pie data={data} dataKey="y" nameKey="name" outerRadius={80} innerRadius={40} paddingAngle={2}>
          {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
        </Pie>
        <Tooltip contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:10,boxShadow:"var(--shadow-lg)",fontSize:12}}/>
        <Legend wrapperStyle={{fontSize:11}}/>
      </PieChart>
    );
  }
  return null;
}
